<template>
  <div class="min-h-screen bg-white">
    <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 class="text-4xl md:text-5xl font-marketing font-bold text-primary mb-8">Checkout</h1>

      <div v-if="error" class="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
        <p class="text-sm text-red-700 font-sans">{{ error }}</p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div class="border border-gray-200 rounded-lg p-8 bg-creme-50">
          <h2 class="text-2xl font-marketing font-bold text-primary mb-4">Login</h2>
          <p class="text-sm text-gray-600 font-sans mb-6">Sign in to your account to continue</p>
          
          <form @submit.prevent="handleLogin" class="space-y-4">
            <div>
              <label for="email" class="block text-sm font-marketing font-medium text-primary mb-1">
                Email address
              </label>
              <input
                id="email"
                v-model="email"
                type="email"
                required
                class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black placeholder-gray-500 sm:text-sm font-sans"
                placeholder="your@email.com"
              />
            </div>
            
            <div>
              <label for="password" class="block text-sm font-marketing font-medium text-primary mb-1">
                Password
              </label>
              <input
                id="password"
                v-model="password"
                type="password"
                required
                class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black placeholder-gray-500 sm:text-sm font-sans"
                placeholder="Enter your password"
              />
            </div>

            <div v-if="loginError" class="text-sm text-red-600 font-sans">
              {{ loginError }}
            </div>

            <button
              type="submit"
              :disabled="loginLoading"
              class="w-full py-2 px-4 border border-transparent text-sm font-marketing font-medium rounded-md text-white bg-accent hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ loginLoading ? 'Signing in...' : 'Sign in and Continue' }}
            </button>
          </form>

          <div class="mt-4 text-center">
            <p class="text-sm text-gray-600 font-sans">
              <a href="/register" class="font-marketing font-medium text-accent hover:text-green-700 transition-colors">
                Create an account
              </a>
            </p>
          </div>
        </div>

        <div class="border border-gray-200 rounded-lg p-8 bg-white">
          <h2 class="text-2xl font-marketing font-bold text-primary mb-4">Continue as Guest</h2>
          <p class="text-sm text-gray-600 font-sans mb-6">Complete your purchase without creating an account</p>
          
          <button
            @click="continueAsGuest"
            class="w-full py-2 px-4 border-2 border-accent text-sm font-marketing font-medium rounded-md text-accent bg-white hover:bg-creme-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-colors"
          >
            Continue to Checkout
          </button>

          <div class="mt-6 pt-6 border-t border-gray-200">
            <p class="text-xs text-gray-500 font-sans">
              You can create an account after checkout to track your orders and manage your information.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { authService } from '../services/auth'

const email = ref('')
const password = ref('')
const loginError = ref('')
const loginLoading = ref(false)
const error = ref('')

const handleLogin = async () => {
  loginError.value = ''
  loginLoading.value = true
  
  try {
    const result = await authService.login(email.value, password.value)
    
    if (result.success) {
      window.location.href = '/checkout'
    } else {
      loginError.value = typeof result.error === 'string' ? result.error : 'Login failed. Please try again.'
    }
  } catch (err) {
    loginError.value = 'An unexpected error occurred. Please try again.'
  } finally {
    loginLoading.value = false
  }
}

const continueAsGuest = () => {
  window.location.href = '/checkout'
}

onMounted(() => {
  if (authService.isAuthenticated()) {
    window.location.href = '/checkout'
  }
})
</script>

<style scoped></style>
